package org.teddapp.project.ui.backup

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.db.nowMillis
import org.teddapp.project.sync.SyncStatus
import org.teddapp.project.sync.SyncTableStatus
import org.teddapp.project.sync.SyncUiState
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/** « il y a 3 min », « il y a 2 h », « hier », « jamais ». */
internal fun relativeSince(epochMs: Long?, now: Long = nowMillis()): String {
    if (epochMs == null || epochMs <= 0L) return "jamais"
    val s = ((now - epochMs) / 1000L).coerceAtLeast(0L)
    return when {
        s < 60 -> "à l'instant"
        s < 3_600 -> "il y a ${s / 60} min"
        s < 86_400 -> "il y a ${s / 3_600} h"
        s < 172_800 -> "hier"
        else -> "il y a ${s / 86_400} j"
    }
}

/** Nombre avec espace fine des milliers : 5 874 → « 5 874 ». */
internal fun groupThousands(n: Long): String {
    val raw = kotlin.math.abs(n).toString()
    val sb = StringBuilder()
    raw.reversed().forEachIndexed { i, c -> if (i > 0 && i % 3 == 0) sb.append(' '); sb.append(c) }
    return (if (n < 0) "-" else "") + sb.reverse().toString()
}

/**
 * Le tableau de bord de la synchronisation, table par table : ce qui attend, quand la
 * table est passée pour la dernière fois, la dernière erreur, et, après vérification,
 * le compte du serveur en regard du local.
 *
 * C'est l'écran qui aurait dit ce matin que rien ne partait depuis trois heures.
 */
@Composable
internal fun SyncTablesDashboard(
    state: SyncUiState,
    cloudAvailable: Boolean,
    onCheck: () -> Unit,
    onSyncNow: () -> Unit,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    val tables = state.tables
    val troubled = tables.filter { !it.healthy }
    val ordered = troubled + tables.filter { it.healthy }
    val pending = tables.sumOf { it.pending }
    val gapTotal = tables.mapNotNull { it.gap }.sumOf { it }

    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Column(modifier = Modifier.weight(1f)) {
                if (compact) Text("TABLES · ${tables.size}", style = TeddType.Overline, color = TeddTokens.InkMuted)
                else Text("Tables", style = TeddType.Title1, color = TeddTokens.Ink)
                Text(
                    text = when {
                        !cloudAvailable -> "Synchronisation désactivée."
                        state.status == SyncStatus.Syncing -> "Cycle en cours…"
                        pending == 0 && troubled.isEmpty() -> "Tout est passé. Dernier cycle ${relativeSince(state.lastSyncAt)}."
                        pending > 0 -> "${groupThousands(pending.toLong())} lignes attendent d'être envoyées. Dernier cycle réussi ${relativeSince(state.lastSyncAt)}."
                        else -> "${troubled.size} table(s) en difficulté. Dernier cycle réussi ${relativeSince(state.lastSyncAt)}."
                    },
                    style = TeddType.BodyLg,
                    color = if (troubled.isEmpty()) TeddTokens.InkMuted else TeddTokens.Ink,
                    fontSize = 14.sp,
                )
                state.nextRetryAt?.takeIf { it > nowMillis() }?.let { at ->
                    val minutes = ((at - nowMillis()) / 60_000L).coerceAtLeast(0L)
                    Text(
                        text = "${state.consecutiveFailures} échec(s) d'affilée · prochaine tentative automatique " +
                            (if (minutes < 1) "dans moins d'une minute" else "dans $minutes min") + ". « Envoyer » n'attend pas.",
                        style = TeddType.Mono, color = TeddTokens.Warning, fontSize = 11.5.sp,
                    )
                }
                state.remoteCheckedAt?.let { at ->
                    Text(
                        text = if (gapTotal == 0L) "Serveur vérifié ${relativeSince(at)} : mêmes comptes qu'ici."
                        else "Serveur vérifié ${relativeSince(at)} : ${groupThousands(kotlin.math.abs(gapTotal))} lignes d'écart.",
                        style = TeddType.Mono, color = if (gapTotal == 0L) TeddTokens.Success else TeddTokens.Warning, fontSize = 11.5.sp,
                    )
                }
            }
            if (cloudAvailable) {
                TeddButton(
                    text = if (state.checkingRemote) "Vérification…" else "Vérifier avec le serveur",
                    onClick = onCheck,
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    enabled = !state.checkingRemote,
                )
                if (pending > 0 && state.status != SyncStatus.Syncing) {
                    TeddButton(text = "Envoyer", onClick = onSyncNow, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm)
                }
            }
        }

        if (tables.isEmpty()) {
            Text("Comptage en cours…", color = TeddTokens.InkMuted, fontSize = 12.5.sp)
            return
        }

        // En-tête des colonnes, hors compact.
        if (!compact) {
            Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Text("TABLE", style = TeddType.Overline, color = TeddTokens.InkSubtle, modifier = Modifier.weight(1f))
                Text("EN ATTENTE", style = TeddType.Overline, color = TeddTokens.InkSubtle, modifier = Modifier.width(84.dp))
                Text("ICI", style = TeddType.Overline, color = TeddTokens.InkSubtle, modifier = Modifier.width(64.dp))
                Text("SERVEUR", style = TeddType.Overline, color = TeddTokens.InkSubtle, modifier = Modifier.width(132.dp))
                Text("DERNIER PASSAGE", style = TeddType.Overline, color = TeddTokens.InkSubtle, modifier = Modifier.width(120.dp))
            }
        }
        ordered.forEach { t -> TableRow(t, compact) }
    }
}

@Composable
private fun TableRow(t: SyncTableStatus, compact: Boolean) {
    val tone = when {
        t.lastError != null -> TeddTokens.Danger
        t.pending > 0 || (t.gap ?: 0L) != 0L -> TeddTokens.Warning
        else -> TeddTokens.Success
    }
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = androidx.compose.foundation.layout.PaddingValues(horizontal = 14.dp, vertical = 10.dp), tonal = !t.healthy) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.weight(1f)) {
                // Un point de couleur dit l'état : vert passé, ambre en attente ou en écart, rouge en erreur.
                androidx.compose.foundation.layout.Box(
                    modifier = Modifier.size(8.dp).clip(RoundedCornerShape(TeddTokens.RFull)).background(tone, RoundedCornerShape(TeddTokens.RFull)),
                )
                Text(t.entity.tableName, color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.Medium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            if (compact) {
                Text(
                    text = buildString {
                        if (t.pending > 0) append("${groupThousands(t.pending.toLong())} en attente") else append("à jour")
                        t.gap?.let { g -> if (g != 0L) append(" · ${if (g > 0) "+" else ""}${groupThousands(g)} ici") }
                    },
                    style = TeddType.Mono, color = tone, fontSize = 11.5.sp,
                )
            } else {
                Text(
                    if (t.pending == 0) "0" else groupThousands(t.pending.toLong()),
                    style = TeddType.Mono, color = if (t.pending > 0) TeddTokens.Warning else TeddTokens.InkMuted, fontSize = 12.sp,
                    fontWeight = if (t.pending > 0) FontWeight.Bold else FontWeight.Normal, modifier = Modifier.width(84.dp),
                )
                Text(groupThousands(t.total.toLong()), style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp, modifier = Modifier.width(64.dp))
                Text(
                    text = t.remoteCount?.let { r ->
                        val g = t.gap ?: 0L
                        if (g == 0L) groupThousands(r) else "${groupThousands(r)} (${if (g > 0) "+" else ""}${groupThousands(g)} ici)"
                    } ?: "—",
                    style = TeddType.Mono, color = if ((t.gap ?: 0L) != 0L) TeddTokens.Warning else TeddTokens.InkMuted, fontSize = 12.sp,
                    modifier = Modifier.width(132.dp), maxLines = 1, overflow = TextOverflow.Ellipsis,
                )
                Text(relativeSince(t.lastOkAt), style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp, modifier = Modifier.width(120.dp))
            }
        }
        t.lastError?.let { err ->
            Text(
                text = "Dernière erreur : $err",
                color = TeddTokens.Danger,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 6.dp).widthIn(max = 720.dp),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}
